<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue'
import { useStore } from 'vuex'
import PlotlyLineChart from '@/components/plotly-chart/PlotlyLineChart.vue'

const props = defineProps({
  hoverItem: Object,
  stabSchematisationStage: { type: Object, required: true },
  refPrefix: { type: String, default: '' },
  ignoreHeight: { type: String, default: '0px' }
})

const store = useStore()

const overviewPlot = ref(null)

const selectedStabSchematisationStageId = computed(
  () => store.state.dikeProfiles.selectedStabSchematisationStageId
)
const expand = computed(() => store.state.dikeProfiles.expand)
const drawer = computed(() => store.state.project.drawer)
const showComments = computed(() => store.state.comment.show)

const plotData = computed(() => store.getters['overview/getPlotData'])
const getStabSchematisationStageById = computed(
  () => store.getters['stabSchematisationStage/getStabSchematisationStageById']
)

const currentTopoProfile = computed(() => store.state.topoProfile.currentItem)
const topoProfilePointItemsOverview = computed(() => store.state.topoProfilePoint.itemsOverview)
const options = computed(() => store.state.topoProfilePoint.options)
const topoProfileItemsOverview = computed(() => store.state.topoProfile.itemsOverview)

const overViewPlotData = computed(() =>
  plotData.value(getStabSchematisationStageById.value(selectedStabSchematisationStageId.value))
)

const getChartYAxisRange = computed(() => (id) => {
  return store.getters['topoProfilePoint/getChartYAxisRange'](id)
})

const refPlot = computed(() => `${props.refPrefix}overview-${props.stabSchematisationStage.id}`)

function getDisplayName(name) {
  return options.value?.name?.choices.find((choice) => choice.value === name)?.display_name
}

const annotations = computed(() => {
  const selectedTopoProfile = topoProfileItemsOverview.value.find(
    (item) => item.stab_schematisation_stage === selectedStabSchematisationStageId.value
  )
  if (!selectedTopoProfile) {
    return []
  }
  const itemsAnnotate = topoProfilePointItemsOverview.value.filter(
    (item) => item.properties.name && item.properties.topo_profile === selectedTopoProfile.id
  )
  return itemsAnnotate.map((item) => {
    let displayName = getDisplayName(item.properties.name)
    displayName = displayName ? displayName : item.properties.name
    return {
      x: item.geometry.coordinates[0],
      y: item.geometry.coordinates[1],
      yshift: 10,
      yanchor: 'bottom',
      textangle: '90',
      bgcolor: 'white',
      text: displayName,
      showarrow: false,
      ay: 40,
      hideToggle: true
    }
  })
})

const plotRelayout = async () => await overviewPlot.value?.relayout()

watch(drawer, () => plotRelayout())
watch(expand, () => plotRelayout())
watch(showComments, () => plotRelayout())

defineExpose({
  plotRelayout
})

onMounted(() => {
  plotRelayout()
})
</script>

<template>
  <div class="chart-container">
    <PlotlyLineChart
      v-if="stabSchematisationStage"
      :key="stabSchematisationStage.id"
      :data="overViewPlotData"
      :responsive="false"
      :showAnimate="false"
      :annotations="[...annotations]"
      :refPlot="refPlot"
      ref="overviewPlot"
      :yAxisRange="getChartYAxisRange(currentTopoProfile?.id)"
    />
  </div>
</template>

<style scoped>
.chart-container {
  height: calc(100vh - v-bind(ignoreHeight));
}
</style>
